import { Injectable } from '@angular/core';
import { FormArray, FormGroup, AbstractControl, ValidationErrors } from '@angular/forms';
import { CompanySettingsUpdateData, CompanySettingsData } from '../interfaces';
import { COMPANY_SETTINGS_STSTEM_ID } from '../enums';

@Injectable({
  providedIn: 'root'
})
export class AdditionalService {
  private readonly HIDE_PASSWORD_DELAY: number = 3000;
  passwordVisibility: Record<string, boolean> = {};
  private visibilityTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  constructor() {}

  togglePasswordVisibility(fieldName: string): void {
    this.passwordVisibility[fieldName] = !this.passwordVisibility[fieldName];

    if (this.visibilityTimers[fieldName]) {
      clearTimeout(this.visibilityTimers[fieldName]);
    }

    this.visibilityTimers[fieldName] = setTimeout(() => {
      this.passwordVisibility[fieldName] = false;
    }, this.HIDE_PASSWORD_DELAY);
  }

  isPasswordVisible(fieldName: string): boolean {
    return !!this.passwordVisibility[fieldName];
  }

  passwordMatchValidator(passwordField: string, confirmPasswordField: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get(passwordField)?.value;
      const confirmPassword = control.get(confirmPasswordField)?.value;

      return password === confirmPassword ? null : { passwordMismatch: true };
    };
  }

  sortData<T>(data: T[], sortField: keyof T, direction: 'asc' | 'desc' | ''): T[] {
    if (!sortField || !direction) {
      return [...data];
    }

    return [...data].sort((a, b) => {
      const valueA = a[sortField];
      const valueB = b[sortField];

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return direction === 'asc' ? valueA - valueB : valueB - valueA;
      }

      if (valueA instanceof Date && valueB instanceof Date) {
        return direction === 'asc' ? valueA.getTime() - valueB.getTime() : valueB.getTime() - valueA.getTime();
      }

      return 0;
    });
  }

  cleanFormData(formValue: any): any {
    if (Array.isArray(formValue)) {
      return formValue.map((item) => this.cleanFormData(item));
    } else if (formValue !== null && typeof formValue === 'object') {
      return Object.entries(formValue).reduce((acc, [key, value]) => {
        // Check if the value is a Date, and leave it as is
        if (value instanceof Date) {
          acc[key] = value; // Keep dates unchanged
        } else {
          acc[key] = value === '' ? null : this.cleanFormData(value);
        }
        return acc;
      }, {} as any);
    }
    return formValue;
  }

  markFormGroupAsDirty(form: FormGroup): void {
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control instanceof FormArray) {
        this.markFormArrayAsDirty(control);
      } else if (control instanceof FormGroup) {
        this.markFormGroupAsDirty(control);
      } else {
        control?.markAsDirty();
      }
    });
  }

  private markFormArrayAsDirty(formArray: FormArray): void {
    formArray.controls.forEach((arrayControl: AbstractControl) => {
      arrayControl.markAsDirty();

      if (arrayControl instanceof FormGroup) {
        this.markFormGroupAsDirty(arrayControl);
      } else if (arrayControl instanceof FormArray) {
        this.markFormArrayAsDirty(arrayControl);
      }
    });
  }

  scrollToFirstInvalidControl(form: FormGroup): void {
    const invalidControl = this.findFirstInvalidElement();
    if (invalidControl) {
      invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      invalidControl.focus();
    }
  }

  private findFirstInvalidElement(): HTMLElement | null {
    return document.querySelector('.ng-invalid:not(form):not(fieldset)') as HTMLElement;
  }

  getCompanySettingsOptionValue(item: CompanySettingsData) {
    if (!item) return null;
    return {
      systemId: item?.systemId !== COMPANY_SETTINGS_STSTEM_ID.CUSTOM ? item?.systemId : null,
      customId: item?.id
    };
  }

  compareCompanySettingsOptions = (o1: CompanySettingsUpdateData, o2: CompanySettingsUpdateData) => {
    return o1 && o2 && o1.systemId === o2.systemId && o1.customId === o2.customId;
  };

  maxPlainTextLength(maxLength: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const plainText = control.value.replace(/<[^>]*>/g, '').trim(); // remove HTML
      return plainText.length > maxLength ? { maxlength: { requiredLength: maxLength, actualLength: plainText.length } } : null;
    };
  }

  allowOnlyDigits(event: Event, form: FormGroup, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const inputValue = input.value;
    const sanitizedValue = inputValue.replace(/[^+\d]/g, '');

    if (sanitizedValue !== inputValue) {
      form.get(controlName)?.setValue(sanitizedValue, { emitEvent: false });
    }
  }
}
